<template>
  <div v-if="isOfflineMode" class="offline-sync-status">
    <!-- Offline Mode Indicator -->
    <v-card v-if="showStatus" class="sync-status-card">
      <v-card-text class="pa-2">
        <v-row align="center" no-gutters>
          <v-col cols="auto">
            <v-icon small class="mr-2">mdi-wifi-off</v-icon>
          </v-col>
          <v-col>
            <span class="caption">Offline Mode</span>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Sync Status Bar -->
    <v-card v-if="syncStatus && syncStatus.total > 0" class="sync-status-bar mt-2">
      <v-card-text class="pa-2">
        <v-row align="center" no-gutters>
          <v-col>
            <div class="caption mb-1">
              Courses to sync: {{ syncStatus.pending }} 
              <span v-if="syncStatus.failed > 0" class="error--text">
                ({{ syncStatus.failed }} failed)
              </span>
            </div>
            <div class="caption mb-1" v-if="certificateSyncStatus && certificateSyncStatus.pending_updates > 0">
              Certificates to sync: {{ certificateSyncStatus.pending_updates }}
            </div>
            <v-progress-linear 
              v-if="syncStatus.pending > 0" 
              :value="syncProgress" 
              height="4" 
              color="warning"
            ></v-progress-linear>
          </v-col>
          <v-col cols="auto">
            <v-btn 
              v-if="!isSyncing && syncStatus.pending > 0" 
              @click="startSync" 
              x-small 
              color="primary"
              :disabled="!isOnline"
            >
              <v-icon small>mdi-sync</v-icon>
            </v-btn>
            <v-progress-circular v-if="isSyncing" indeterminate size="20" width="2"></v-progress-circular>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'OfflineSyncStatus',
  data() {
    return {
      showStatus: true,
      syncStatus: null,
      certificateSyncStatus: null,
      isSyncing: false,
      isOnline: navigator.onLine,
      refreshInterval: null
    }
  },
  computed: {
    isOfflineMode() {
      return window && window.electron;
    },
    syncProgress() {
      if (!this.syncStatus || this.syncStatus.total === 0) return 100;
      return ((this.syncStatus.total - this.syncStatus.pending) / this.syncStatus.total) * 100;
    }
  },
  mounted() {
    if (this.isOfflineMode) {
      this.updateSyncStatus();
      this.refreshInterval = setInterval(() => {
        this.updateSyncStatus();
      }, 5000);

      // Listen for online/offline events
      window.addEventListener('online', this.onOnline);
      window.addEventListener('offline', this.onOffline);
    }
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
  },
  methods: {
    async updateSyncStatus() {
      try {
        const { offlineCourseStorage } = await import('@/services/offline-course-storage');
        this.syncStatus = await offlineCourseStorage.getSyncStatus();
        
        // Certificate sync status - simplified
        this.certificateSyncStatus = { pending_updates: 0 };
      } catch (error) {
        console.error('Failed to get sync status:', error);
      }
    },
    async startSync() {
      if (this.isSyncing || !this.isOnline) return;
      
      try {
        this.isSyncing = true;
        const axios = (await import('axios')).default;
        
        let courseResults = [];
        let certificateResults = [];
        
        // Sync courses
        try {
          const { offlineCourseStorage } = await import('@/services/offline-course-storage');
          courseResults = await offlineCourseStorage.syncAllPendingCourses(axios);
          console.log('Course sync completed:', courseResults);
        } catch (error) {
          console.error('Course sync failed:', error);
        }
        
        // Certificate sync - simplified (no-op)
        certificateResults = [];
        
        // Update sync status
        await this.updateSyncStatus();
        
        // Show success message
        const courseSuccessCount = courseResults.filter(r => r.success).length;
        const courseFailureCount = courseResults.filter(r => !r.success).length;
        const certSuccessCount = certificateResults.filter(r => r.success).length;
        const certFailureCount = certificateResults.filter(r => !r.success).length;
        
        const totalSuccess = courseSuccessCount + certSuccessCount;
        const totalFailure = courseFailureCount + certFailureCount;
        
        let message = '';
        if (totalSuccess > 0) {
          const parts = [];
          if (courseSuccessCount > 0) parts.push(`${courseSuccessCount} courses`);
          if (certSuccessCount > 0) parts.push(`${certSuccessCount} certificates`);
          message = `Synced ${parts.join(' and ')} successfully`;
        }
        
        if (totalFailure > 0) {
          message += totalSuccess > 0 ? `, ${totalFailure} failed` : `${totalFailure} items failed to sync`;
        }
        
        if (!message) {
          message = 'No items to sync';
        }
        
        this.$store.dispatch('showSnackbar', {
          message,
          color: totalFailure > 0 ? 'warning' : 'success'
        });
        
      } catch (error) {
        console.error('Sync failed:', error);
        this.$store.dispatch('showSnackbar', {
          message: 'Sync failed: ' + error.message,
          color: 'error'
        });
      } finally {
        this.isSyncing = false;
      }
    },
    onOnline() {
      this.isOnline = true;
      console.log('Connection restored - manual sync available from Network Settings');

      // DISABLED: Auto-sync when coming online - use manual sync from Network Settings instead
      // setTimeout(() => {
      //   const hasPendingCourses = this.syncStatus && this.syncStatus.pending > 0;
      //   const hasPendingCertificates = this.certificateSyncStatus && this.certificateSyncStatus.pending_updates > 0;
      //
      //   if (hasPendingCourses || hasPendingCertificates) {
      //     this.startSync();
      //   }
      // }, 2000);
    },
    onOffline() {
      this.isOnline = false;
      console.log('Connection lost - offline mode active');
    }
  }
}
</script>

<style scoped>
.offline-sync-status {
  position: fixed;
  top: 80px;
  right: 16px;
  z-index: 1000;
  width: 220px;
  opacity: 0.7;
}

.sync-status-card, .sync-status-bar {
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
}

.sync-status-card {
  background-color: rgba(33, 150, 243, 0.1) !important;
  color: #666 !important;
}

.sync-status-bar {
  background-color: rgba(255, 193, 7, 0.05) !important;
  color: #555 !important;
}

.caption {
  font-size: 11px;
  line-height: 1.2;
}
</style>
